/**
 * API-Kompatibilitätsprüfung zwischen Frontend und Backend
 */

import { isFetchableBackendBase, normalizeBackendApiBase } from './backendBaseUrl';

/**
 * Vergleicht zwei semantische Versionen (MAJOR.MINOR.PATCH)
 * @param {string} v1 - Erste Version (z.B. "0.1.58")
 * @param {string} v2 - Zweite Version (z.B. "0.1.59")
 * @returns {number} -1 wenn v1 < v2, 0 wenn v1 == v2, 1 wenn v1 > v2
 */
export function compareVersions(v1, v2) {
  const parseVersion = (v) => {
    const parts = v.trim().replace(/^v/, '').split('.').map(Number);
    return {
      major: parts[0] || 0,
      minor: parts[1] || 0,
      patch: parts[2] || 0
    };
  };

  const ver1 = parseVersion(v1);
  const ver2 = parseVersion(v2);

  if (ver1.major !== ver2.major) {
    return ver1.major < ver2.major ? -1 : 1;
  }
  if (ver1.minor !== ver2.minor) {
    return ver1.minor < ver2.minor ? -1 : 1;
  }
  if (ver1.patch !== ver2.patch) {
    return ver1.patch < ver2.patch ? -1 : 1;
  }
  return 0;
}

/**
 * Lädt Kompatibilitäts-Informationen vom Backend
 * @param {string} backendUrl - Backend-URL
 * @param {string} frontendVersion - Frontend-Version
 * @returns {Promise<Object>} Kompatibilitäts-Informationen
 */
export async function getCompatibilityInfo(backendUrl, frontendVersion) {
  try {
    if (!isFetchableBackendBase(backendUrl)) {
      return {
        success: false,
        error: 'Backend-URL unvollständig oder ungültig',
        compatible: true,
        backend_version: null,
        warnings: [],
      };
    }
    const normalizedUrl = normalizeBackendApiBase(backendUrl);
    const url = `${normalizedUrl}/api/compatibility?frontend_version=${encodeURIComponent(frontendVersion)}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      success: true,
      ...data
    };
  } catch (error) {
    console.error('[Compatibility] Fehler beim Laden der Kompatibilitäts-Info:', error);
    return {
      success: false,
      error: error.message,
      compatible: true, // Im Fehlerfall als kompatibel annehmen (Graceful Degradation)
      backend_version: null,
      warnings: [`Kompatibilitätsprüfung fehlgeschlagen: ${error.message}`]
    };
  }
}

/**
 * Prüft API-Kompatibilität zwischen Frontend und Backend
 * @param {string} backendUrl - Backend-URL
 * @param {string} frontendVersion - Frontend-Version
 * @returns {Promise<Object>} Kompatibilitäts-Ergebnis
 */
export async function checkApiCompatibility(backendUrl, frontendVersion) {
  if (!backendUrl || !frontendVersion) {
    return {
      compatible: true, // Wenn keine Info verfügbar, als kompatibel annehmen
      warning: 'Backend-URL oder Frontend-Version nicht verfügbar'
    };
  }

  try {
    const compatibilityInfo = await getCompatibilityInfo(backendUrl, frontendVersion);
    
    return {
      compatible: compatibilityInfo.compatible !== false, // Default: true
      backendVersion: compatibilityInfo.backend_version,
      warnings: compatibilityInfo.warnings || [],
      features: compatibilityInfo.features || {},
      recommendedVersion: compatibilityInfo.recommended_frontend_version
    };
  } catch (error) {
    console.warn('[Compatibility] Kompatibilitätsprüfung fehlgeschlagen:', error);
    // Im Fehlerfall als kompatibel annehmen (Graceful Degradation)
    return {
      compatible: true,
      warning: `Kompatibilitätsprüfung nicht möglich: ${error.message}`
    };
  }
}

/**
 * Prüft ob eine Frontend-Version mit einer Backend-Version kompatibel ist
 * (Fallback wenn Backend-Endpoint nicht verfügbar ist)
 * @param {string} frontendVersion - Frontend-Version
 * @param {string} backendVersion - Backend-Version
 * @returns {boolean} true wenn kompatibel
 */
export function isVersionCompatible(frontendVersion, backendVersion) {
  if (!frontendVersion || !backendVersion) {
    return true; // Wenn keine Info verfügbar, als kompatibel annehmen
  }

  // Einfache Heuristik: Gleiche Major-Version = kompatibel
  const frontendMajor = parseInt(frontendVersion.split('.')[0]) || 0;
  const backendMajor = parseInt(backendVersion.split('.')[0]) || 0;

  // Gleiche Major-Version oder Frontend ist neuer (Minor/Patch)
  if (frontendMajor === backendMajor) {
    return true;
  }

  // Frontend ist eine Major-Version älter als Backend (könnte noch funktionieren)
  if (frontendMajor === backendMajor - 1) {
    return true; // Vorsichtshalber als kompatibel annehmen
  }

  // Großer Unterschied = möglicherweise inkompatibel
  return false;
}
